import React, { useState, useEffect } from 'react';
import SaleBatchDetailsModal from '../components/SaleBatchDetailsModal';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import Pagination from '../components/Pagination';
import { FaSearch } from 'react-icons/fa';

const Chiqimlar = () => {
  const [saleBatches, setSaleBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [batchStoreMap, setBatchStoreMap] = useState({}); // { SellBatchID: StoreID }
  const [stores, setStores] = useState([]); // storehouses for filter and name mapping
  const [storesMap, setStoresMap] = useState({}); // { id: name }
  const [staffs, setStaffs] = useState([]); // only for owner

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [customerType, setCustomerType] = useState(''); // '', 'odatiy', 'nasiya', 'ulgurchi'
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState('');

  // Barcode search state
  const [barcode, setBarcode] = useState('');

  const { user, token } = useAuth();

  // Initial data
  useEffect(() => {
    // Preselect staff if signed in as staff
    if (user?.role === 'staff' && user?.id) {
      setSelectedStaffId(String(user.id));
    }
    fetchStores();
    if (user?.role === 'owner') {
      fetchStaffs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Replace useEffect for pagination/filters with updated fetch logic
  useEffect(() => {
    fetchSaleBatches();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, customerType, selectedStaffId, selectedStoreId, dateFrom, dateTo]);

  // After saleBatches loaded, fetch store info for each batch
  useEffect(() => {
    const fetchStoresForBatches = async () => {
      if (!Array.isArray(saleBatches) || saleBatches.length === 0) return;
      const map = {};
      await Promise.all(
        saleBatches.map(async (batch) => {
          try {
            const res = await fetch(`/api/sale-history/by-sellbatch/${batch.SellBatchID}`, {
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                'x-owner-id': user?.ownerId || user?.id || '',
                'x-staff-id': user?.staffId || ''
              }
            });
            if (!res.ok) return;
            const items = await res.json();
            if (Array.isArray(items) && items.length > 0) {
              map[batch.SellBatchID] = items[0].StoreID;
            }
          } catch {}
        })
      );
      setBatchStoreMap(map);
    };
    fetchStoresForBatches();
  }, [saleBatches]);

  const handleBatchClick = (batch) => {
    setSelectedBatch(batch);
    setShowDetailsModal(true);
  };

  const handleBarcodeInput = (e) => setBarcode(e.target.value);

  const handleBarcodeSearch = () => {
    setCurrentPage(1);
    fetchSaleBatches(1, itemsPerPage, true); // start new search from page 1
  };

  const handleBarcodeClear = () => {
    setBarcode('');
    setCurrentPage(1);
    fetchSaleBatches(1, itemsPerPage, false); // revert to normal batches
  };

  const formatCurrency = (amount, currency) => {
    if (amount == null || currency == null) return 'N/A';
    return `${Number(amount).toLocaleString()} ${currency}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const dateObj = new Date(dateString);
      return dateObj.toLocaleDateString('uz-UZ') + ' ' + dateObj.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch (error) {
      return dateString;
    }
  };

  const getCustomerTypeLabel = (customerType) => {
    if (!customerType || customerType === 'customer1') return 'Odatiy';
    switch (customerType) {
      case 'customer2': return 'Nasiya';
      case 'customer3': return 'Ulgurchi';
      default: return customerType || "Noma'lum";
    }
  };

  const getDoerLabel = (batch) => {
    if (!batch || batch.StaffID == null) return 'EGA'; // owner did the sale
    return batch.StaffFullName || `Xodim #${batch.StaffID}`;
  };

  const getBatchTotalWithDelivery = (batch) => {
    if (!batch) return 0;
    const total = Number(batch.TotalBatchPrice) || 0;
    const delivery = batch.HasDelivery ? (Number(batch.DeliveryPrice) || 0) : 0;
    return total + delivery;
  };

  const fetchStores = async () => {
    try {
      // Staff: only show stores where view permission is true
      if (user?.role === 'staff') {
        const res = await fetch(`/api/store-permissions/view-stores`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
            'x-owner-id': user?.ownerId || user?.id || '',
            'x-staff-id': user?.staffId || ''
          }
        });
        if (!res.ok) return;
        const arr = await res.json();
        const items = Array.isArray(arr) ? arr : [];
        setStores(items);
        const map = {};
        items.forEach(s => { if (s?.id != null) map[s.id] = s.name; });
        setStoresMap(map);
        // Default to first permitted store; if none permitted, clear selection and sales
        if (!selectedStoreId) {
          if (items.length > 0) {
            setSelectedStoreId(String(items[0].id));
          } else {
            setSelectedStoreId('');
            setSaleBatches([]);
            setTotalItems(0);
          }
        } else if (items.length > 0 && !items.some(s => String(s.id) === String(selectedStoreId))) {
          // Previously selected store no longer permitted; switch to first permitted
          setSelectedStoreId(String(items[0].id));
        }
        return;
      }

      // Owner: show all owner's stores
      const res = await fetch(`/api/stores?page=1&limit=1000`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        }
      });
      if (!res.ok) return;
      const json = await res.json();
      const items = Array.isArray(json?.items) ? json.items : [];
      setStores(items);
      const map = {};
      items.forEach(s => { if (s?.id != null) map[s.id] = s.name; });
      setStoresMap(map);
    } catch (e) {
      // ignore
    }
  };

  const fetchStaffs = async () => {
    try {
      const res = await fetch(`/api/staffs`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        }
      });
      if (!res.ok) return;
      const json = await res.json();
      if (Array.isArray(json)) setStaffs(json);
    } catch (e) {
      // ignore
    }
  };

  const fetchSaleBatches = async (
    customPage = currentPage, customItemsPerPage = itemsPerPage, customIsBarcodeSearch = false
  ) => {
    setLoading(true);
    try {
      // Staff must be restricted to permitted storehouses only
      if (user?.role === 'staff' && !selectedStoreId && !barcode) {
        setSaleBatches([]);
        setTotalItems(0);
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set('page', String(customPage));
      params.set('limit', String(customItemsPerPage));
      if (customerType) params.set('customerType', customerType);
      if (user?.role === 'owner' && selectedStaffId) params.set('staffId', selectedStaffId);
      if (selectedStoreId) params.set('storeId', selectedStoreId);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if ((customIsBarcodeSearch || barcode) && barcode) {
        params.set('barcode', barcode.trim());
      }
      const response = await fetch(`/api/sale-history/batches?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        }
      });
      if (!response.ok) {
        throw new Error('Chiqimlarni yuklashda xatolik!');
      }
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setSaleBatches(items);
      setTotalItems(Number(data?.total) || 0);
    } catch (err) {
      setSaleBatches([]);
      toast.error(err.message || 'Chiqimlarni yuklashda xatolik!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h1 className="h2">Chiqimlar (Sotuv Partiyalari)</h1>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {/* Customer Type */}
          <select
            className="form-select w-auto"
            value={customerType}
            onChange={(e) => { setCustomerType(e.target.value); setCurrentPage(1); }}
          >
            <option value="">Mijoz turi: Barchasi</option>
            <option value="odatiy">Odatiy</option>
            <option value="nasiya">Nasiya</option>
            <option value="ulgurchi">Ulgurchi</option>
          </select>

          {/* Staff selector */}
          {user?.role === 'owner' ? (
            <select
              className="form-select w-auto"
              value={selectedStaffId}
              onChange={(e) => { setSelectedStaffId(e.target.value); setCurrentPage(1); }}
            >
              <option value="">Xodim: Barchasi</option>
              {staffs.map(st => (
                <option key={st.id} value={st.id}>{st.full_name || st.username || `Staff #${st.id}`}</option>
              ))}
            </select>
          ) : (
            <select className="form-select w-auto" value={selectedStaffId} disabled>
              <option value={user?.id || ''}>Mening sotuvlarim</option>
            </select>
          )}

          {/* Storehouse selector */}
          <select
            className="form-select w-auto"
            value={selectedStoreId}
            onChange={(e) => { setSelectedStoreId(e.target.value); setCurrentPage(1); }}
            disabled={user?.role === 'staff' && stores.length === 0}
          >
            {user?.role === 'owner' && (
              <option value="">Do'kon: Barchasi</option>
            )}
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>

          {/* Date range */}
          <input
            type="date"
            className="form-control w-auto"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
            placeholder="Boshlanish sana"
          />
          <input
            type="date"
            className="form-control w-auto"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
            placeholder="Tugash sana"
          />
                    {/* Existing Yangilash button */}
          <button
            onClick={() => fetchSaleBatches()}
            className="btn btn-success"
            disabled={loading}
          >
            Yangilash
          </button>
          {/* Barcode search input and submit/clear buttons */}
          <div className="d-flex shadow ">
          <input
            type="text"
            className="form-control w-auto"
            placeholder="Shtrix-kod"
            value={barcode}
            onChange={handleBarcodeInput}
            onKeyDown={(e) => { if (e.key === 'Enter') handleBarcodeSearch(); }}
            style={{ minWidth: 120 }}
          />
          <button
            onClick={handleBarcodeSearch}
            className="btn btn-outline-secondary"
            disabled={loading || !barcode.trim()}
          >
            <FaSearch />
          </button>
          {barcode && (
            <button
              onClick={handleBarcodeClear}
              className="btn btn-outline-danger"
              disabled={loading}
            >
              X
            </button>
          )}
          </div>


        </div>
      </div>

      {loading ? (
        <Loader />
      ) : !Array.isArray(saleBatches) || saleBatches.length === 0 ? (
        <div className="alert alert-info text-center">
          Hech qanday sotuv partiyasi topilmadi
        </div>
      ) : (
        <div >
          <div className="table-responsive rounded-4 shadow" > 
          <table className="table table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Mijoz Turi</th>  
                <th>Doer</th>
                <th>Umumiy Narx</th> 
                <th>Soni</th>
                <th>Joy</th>
                <th>Sana</th>

              </tr>
            </thead>
            <tbody>
              {/* Store mapping for Joy column */}
              {saleBatches.map((batch, index) => {
                const storeId = batchStoreMap[batch.SellBatchID];
                const storeName = storeId ? (storesMap[storeId] || storeId) : '';
                return (
                  <tr
                    key={batch.SellBatchID}
                    onClick={() => handleBatchClick(batch)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                    <td>{getCustomerTypeLabel(batch.customer_type)}</td>
                    <td>{getDoerLabel(batch)}</td>
                    <td>{formatCurrency(getBatchTotalWithDelivery(batch), batch.Currency)}</td>
                    <td>{batch.TotalQuantitySold || 0}</td>
                    <td>{storeName}</td>
                    <td>{formatDate(batch.SaleDate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
          />
        </div>
      )}

      {showDetailsModal && selectedBatch && (
        <SaleBatchDetailsModal
          show={showDetailsModal}
          batch={selectedBatch}
          onHide={() => {
            setShowDetailsModal(false);
            setSelectedBatch(null);
          }}
        />
      )}
    </div>
  );
};

export default Chiqimlar;